import { Controller } from '@hotwired/stimulus';
import CurrentPage from '@frontend/app/features/CurrentPage';
import {
  apply as queryParamsApply,
  extract as extractQueryParams,
} from '@frontend/utils/query-params';
import { dispatchCustomEvent } from '@frontend/utils/events';

// NOTE: JS and CSS from https://css-tricks.com/lets-create-a-custom-audio-player/ has been used to
// work around some browser-specific quirks.
export default class extends Controller {
  static targets = [
    'seekSlider',
    'audio',
    'duration',
    'currentTime',
    'playbackSpeedSelect',
  ];

  static values = {
    srcUrl: String,
    durationInSeconds: {
      type: Number,
      default: 0,
    },
    playbackSpeed: {
      type: Number,
      default: 1,
    },
    isPlaying: {
      type: Boolean,
      default: false,
    },
    skipTime: {
      type: Number,
      default: 15,
    },
    mediaTitle: {
      type: String,
      default: null,
    },
    mediaArtist: {
      type: String,
      default: null,
    },
    mediaArtworkUrl: {
      type: String,
      default: null,
    },
  };

  connect() {
    if (CurrentPage.getShareUrl().isActive) {
      const shareUrl = CurrentPage.getShareUrl().url;
      const newSrcUrl = queryParamsApply(
        this.srcUrlValue,
        extractQueryParams(shareUrl)
      );
      this.srcUrlValue = newSrcUrl;
      this.setServingViaShareUrl(true);
    } else {
      this.setServingViaShareUrl(false);
    }

    this.audioTarget.src = this.srcUrlValue;
    this.interval = setInterval(this.whilePlaying.bind(this), 500);
    this.setupEventListeners();
    this.setupMediaSession();
    this.setupMediaDisplay();

    document.body.addEventListener('voice-note-audio-player:playing', this.pause.bind(this));
  }

  disconnect() {
    // clearInterval(this.interval);
    document.removeEventListener("keydown", this.handleKeydown.bind(this))
  }

  setupEventListeners() {
    this.audioTarget.addEventListener('progress', this.handleProgress.bind(this));
    this.seekSliderTarget.addEventListener('input', this.seekSliderInput.bind(this));
    this.seekSliderTarget.addEventListener('change', this.seekSliderChanged.bind(this));
    this.playbackSpeedSelectTarget.addEventListener('change', this.playbackSpeedSelectChanged.bind(this));
    this.audioTarget.addEventListener('pause', this.handleAudioPaused.bind(this));
    this.audioTarget.addEventListener('play', this.handleAudioPlay.bind(this));
    this.audioTarget.addEventListener('ended', this.handleAudioEnded.bind(this));
    document.body.addEventListener("keydown", this.handleKeydown.bind(this));

    // NOTE: Calling setup after loading metadata to use the audio duration from the metadata
    this.audioTarget.addEventListener('loadedmetadata', () => {
      this.durationInSecondsValue = Math.floor(this.audioTarget.duration);
    });
  }

  setupMediaDisplay() {
    this.updateBufferedAmountDisplay();
  }

  setupMediaSession() {
    if ("mediaSession" in navigator) {
      if(this.mediaTitleValue && this.mediaArtistValue && this.mediaArtworkUrlValue) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: this.mediaTitleValue,
          artist: this.mediaArtistValue,
          album: "Krautreporter",
          artwork: [
            {
              src: this.mediaArtworkUrlValue,
              sizes: "512x512",
              type: "image/png",
            }],
        });
      }

      const actionHandlers = [
        ['play',          this.handleMediaSessionAudioPlay.bind(this)],
        ['pause',         this.handleMediaSessionAudioPaused.bind(this)],
        ['previoustrack', () => { /* ... */ }],
        ['nexttrack',     () => { /* ... */ }],
        ['stop',          () => { /* ... */ }],
        ['seekbackward',  this.handleMediaSessionSeek.bind(this)],
        ['seekforward',   this.handleMediaSessionSeek.bind(this)],
        ['seekto',        this.handleMediaSessionSeek.bind(this)],
      ];

      for (const [action, handler] of actionHandlers) {
        try {
          navigator.mediaSession.setActionHandler(action, handler);
        } catch (error) {
          console.log(`The media session action "${action}" is not supported yet.`, error);
        }
      }
    }
  }

  handleKeydown({ key }) {
    switch (key) {
      case "ArrowRight":
        this.jumpForward();
        break;
      case "ArrowLeft":
        this.jumpBack();
        break;
    }
  }

  handleAudioEnded() {
    this.isPlayingValue = false;
  }

  handleAudioPaused() {
    this.isPlayingValue = false;
  }

  handleAudioPlay() {
    this.isPlayingValue = true;
  }

  handleMediaSessionAudioPlay() {
    this.audioTarget.play();
  }

  handleMediaSessionAudioPaused() {
    this.audioTarget.pause();
  }

  handleMediaSessionSeek(details) {
    switch (details.action) {
      case "seekforward":
        this.jumpForward();
        break;
      case "seekbackward":
        this.jumpBack();
        break;
      case "seekto":
        this.audioTarget.currentTime = details.seekTime;
        break;
    }
  }

  handleProgress() {
    this.updateBufferedAmountDisplay();
  }

  isPlayingValueChanged() {
    const status = this.isPlayingValue ? 'playing' : 'stopped';
    dispatchCustomEvent(`audio-player:${status}`)
    this.element.classList.toggle('is-playing', this.isPlayingValue);

    if(this.isPlayingValue && "mediaSession" in navigator) {
      navigator.mediaSession.playbackState = "playing";
    } else {
      navigator.mediaSession.playbackState = "paused";
    }
  }

  togglePlay() {
    if(this.isPlayingValue) {
      this.pause();
    } else {
      this.play();
    }
  }

  play() {
    this.audioTarget.play();
  }

  pause() {
    this.audioTarget.pause();
  }

  jumpBack() {
    this.audioTarget.currentTime = Math.max(this.audioTarget.currentTime - this.skipTimeValue, 0);
  }

  jumpForward() {
    this.audioTarget.currentTime = Math.min(this.audioTarget.currentTime + this.skipTimeValue, this.audioTarget.duration);
  }

  seekSliderInput() {
    this.updateSeekSliderProgress();
    this.currentTimeTarget.textContent = this.calculateTime(
      this.seekSliderTarget.value
    );
  }

  playbackSpeedSelectChanged() {
    this.playbackSpeedValue = parseFloat(this.playbackSpeedSelectTarget.value);
  }

  playbackSpeedValueChanged() {
    this.audioTarget.playbackRate = this.playbackSpeedValue;
    this.playbackSpeedSelectTarget.value = this.playbackSpeedValue;
  }

  seekSliderChanged() {
    this.audioTarget.currentTime = this.seekSliderTarget.value;
    if (!this.audioTarget.paused) {
      this.whilePlaying();
    }
  }

  whilePlaying() {
    const playbackTimeInSeconds = Math.floor(this.audioTarget.currentTime);
    this.seekSliderTarget.value = playbackTimeInSeconds;
    this.currentTimeTarget.textContent = this.calculateTime(
      playbackTimeInSeconds
    );
    this.updateSeekSliderProgress();
  }

  calculateTime(secs) {
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const returnedSeconds = seconds < 10 ? `0${seconds}` : seconds;
    return `${returnedMinutes}:${returnedSeconds}`;
  }

  updateSeekSliderProgress() {
    this.setAudioPlayerProperty(
      'seek-before-width',
      this.percentageAsText(
        this.seekSliderTarget.value,
        this.seekSliderTarget.max
      )
    );
  }

  durationInSecondsValueChanged() {
    this.durationTarget.textContent = this.calculateTime(this.durationInSecondsValue);
    this.seekSliderTarget.max = Math.floor(this.durationInSecondsValue);
  }

  updateBufferedAmountDisplay() {
    if (this.audioTarget.buffered.length === 0) {
      return false;
    }

    const bufferedAmount = Math.floor(
      this.audioTarget.buffered.end(this.audioTarget.buffered.length - 1)
    );
    this.setAudioPlayerProperty(
      'buffered-width',
      this.percentageAsText(bufferedAmount, this.seekSliderTarget.max)
    );
  }

  setAudioPlayerProperty(propertyName, value) {
    this.element.style.setProperty(`--${propertyName}`, value);
  }

  percentageAsText(divided, divisor) {
    return `${(divided / divisor) * 100}%`;
  }

  setServingViaShareUrl(bool) {
    this.element.dataset.testAudioPlayerIsServingViaShareUrl = bool;
  }
}
